import * as THREE from 'three';
import {
  EffectComposer, RenderPass, EffectPass, BloomEffect, ToneMappingEffect, ToneMappingMode,
  VignetteEffect, NoiseEffect, BlendFunction, DepthOfFieldEffect, SMAAEffect, SMAAPreset,
  ChromaticAberrationEffect, BrightnessContrastEffect, HueSaturationEffect,
} from 'postprocessing';
import { N8AOPostPass } from 'n8ao';
import type { QualitySettings } from './Renderer';

/**
 * Chaîne de post-process "cinéma" : HDR → profondeur de champ (mise au point sur ses yeux)
 * → bloom doux → tone mapping AgX → étalonnage léger → vignette + grain.
 */
export class Post {
  composer!: EffectComposer;
  dof?: DepthOfFieldEffect;
  bloom!: BloomEffect;
  readonly focusTarget = new THREE.Vector3(0, 1.55, 0);
  private noise!: NoiseEffect;

  constructor(private renderer: THREE.WebGLRenderer, private scene: THREE.Scene, private camera: THREE.PerspectiveCamera, quality: QualitySettings) {
    this.build(quality);
  }

  build(q: QualitySettings) {
    this.composer?.dispose();
    const composer = new EffectComposer(this.renderer, {
      frameBufferType: THREE.HalfFloatType,
      multisampling: Math.min(q.msaa, this.renderer.capabilities.maxSamples),
    });
    composer.addPass(new RenderPass(this.scene, this.camera));

    // Occlusion ambiante (contact cheveux/peau, creux des yeux, plis) — paliers high/ultra.
    if (q.ssao) {
      const size = this.renderer.getDrawingBufferSize(new THREE.Vector2());
      const ao = new N8AOPostPass(this.scene, this.camera, size.x, size.y);
      ao.configuration.aoRadius = 0.14;
      ao.configuration.distanceFalloff = 1.0;
      ao.configuration.intensity = 2.2;
      ao.configuration.color = new THREE.Color(0x140808);
      ao.configuration.halfRes = q.tier !== 'ultra';
      ao.configuration.gammaCorrection = false;
      ao.setQualityMode(q.tier === 'ultra' ? 'Medium' : 'Low');
      composer.addPass(ao);
    }

    if (q.dof) {
      this.dof = new DepthOfFieldEffect(this.camera, { focusDistance: 1.6, focusRange: 0.55, bokehScale: 2.2, resolutionScale: 0.5 });
      this.dof.target = this.focusTarget;
      composer.addPass(new EffectPass(this.camera, this.dof));
    } else {
      this.dof = undefined;
    }

    this.bloom = new BloomEffect({ intensity: 0.55, luminanceThreshold: 0.78, luminanceSmoothing: 0.25, mipmapBlur: true, radius: 0.72 });
    const tone = new ToneMappingEffect({ mode: ToneMappingMode.AGX });
    const grade = new HueSaturationEffect({ saturation: -0.04, hue: 0 });
    const contrast = new BrightnessContrastEffect({ brightness: 0.0, contrast: 0.06 });
    const vignette = new VignetteEffect({ offset: 0.28, darkness: 0.62 });
    this.noise = new NoiseEffect({ blendFunction: BlendFunction.OVERLAY, premultiply: false });
    this.noise.blendMode.opacity.value = 0.09;
    const ca = new ChromaticAberrationEffect({ offset: new THREE.Vector2(0.00045, 0.00030), radialModulation: true, modulationOffset: 0.35 });

    composer.addPass(new EffectPass(this.camera, this.bloom, tone, grade, contrast, vignette, this.noise));
    composer.addPass(new EffectPass(this.camera, ca));
    if (!q.msaa) composer.addPass(new EffectPass(this.camera, new SMAAEffect({ preset: SMAAPreset.HIGH })));
    this.composer = composer;
  }

  setSize(w: number, h: number) {
    this.composer.setSize(w, h, false);
  }

  render(dt: number) {
    this.composer.render(dt);
  }
}
